import type { Dispatch } from '@reduxjs/toolkit';
import type { GetRootState } from 'js/src/libs/RootState';

import {
    getTranscriptById,
    getTranscriptIds,
    sceneGroupHasTranscriptTranslation,
} from 'js/src/data/transcript/transcript-selectors';
import type { SerializedTranscript, TranscriptData } from 'js/src/data/transcript/transcript-types';
import { reduceTranscriptsToTranslationEditorsMap } from 'js/src/data/translated-editors/translated-editors.utils';
import {
    removeTranslatedEditor,
    storeTranslatedEditors,
} from 'js/src/data/translated-editors/translated-editors-actions';
import { getSceneGroupIdsWithTranslation } from 'js/src/data/translated-editors/translated-editors-selectors';
import { toMutableCopy } from 'js/src/libs/consts';

export const hydrateTranslatedTranscriptsFromRedux = () => {
    return (dispatch: Dispatch, getState: GetRootState) => {
        const state = getState();
        const transcriptIds = getTranscriptIds(state);
        let serializedTranscripts: Record<string, SerializedTranscript> = {};
        serializedTranscripts = transcriptIds.reduce((transcriptMap, transcriptId) => {
            const transcript = getTranscriptById(state, transcriptId);
            if (!transcript) throw new Error(`Transcript with id ${transcriptId} not found`);
            const { editorState, translations, disableOverlays, sceneGroupId } =
                toMutableCopy<TranscriptData>(transcript);
            transcriptMap[sceneGroupId] = { editorState, translations, disableOverlays };
            return transcriptMap;
        }, serializedTranscripts);

        const translationEditors = reduceTranscriptsToTranslationEditorsMap(serializedTranscripts);
        translationEditors.forEach((translationEditor) =>
            dispatch(storeTranslatedEditors(translationEditor)),
        );

        // lookup scene group ids that is no longer in the new dictionary, remove those editors
        const existingSceneGroupIdsWithTranslation = getSceneGroupIdsWithTranslation(state);
        const sceneGroupIdsToRemove = existingSceneGroupIdsWithTranslation.filter(
            (sceneGroupId: string) => !sceneGroupHasTranscriptTranslation(state, sceneGroupId),
        );
        sceneGroupIdsToRemove.forEach((sceneGroupId: string) =>
            dispatch(removeTranslatedEditor({ sceneGroupId })),
        );
    };
};
